import React, { useEffect } from 'react';
import { Route, Switch } from 'react-router-dom';
import SigninRedirectPage from './oauth/SigninRedirectPage';
import SigninRedirectCallbackPage from './oauth/SigninRedirectCallbackPage';
import SignoutRedirectCallbackPage from './oauth/SignoutRedirectCallbackPage';
import Wrapper from '../containers/Wrapper';
import userManager from '../utils/userManager';
import { setDefaultAuthorizationHeader } from '../utils/httpClient';
import { useDispatch } from 'react-redux';
import { isLoggedInToTrue, isLoggedInToFalse, setIsImpersonationSession } from '../actions/AppActions';
import { useIsLoggedIn } from '../hooks/authentication';
import { PATH_LOGIN_REDIRECT, AUTH_ROUTE, PATH_LOGOUT_CALLBACK, PATH_INVITE_CANCELLED, PATH_REDIRECT, PATH_LOGGED_OUT } from '../constants';
import StatementPdfPage from './pdf/StatementPdfPage';
import InvoicePdfPage from './pdf/InvoicePdfPage';
import ErrorPage from './ErrorPage';
import useAppError from '../hooks/useAppError';
import InviteCancelled from './InviteCancelled';
import { ExternalResourceRedirectPage } from '../utils/LazyLoad';

const accessTokenExpired = () => {
  console.warn('Access token expiring...');
  userManager.signoutRedirect();
};

const silentRenewError = (error) => {
  console.error('[OAUTH] Silent Renew Error：', error);
};

const userSignedOut = () => {
  userManager.signoutRedirect();
};

const accessTokenExpiring = () => {
  console.log('Access token is expiring...');
};

const userLoaded = (user) => {
  setDefaultAuthorizationHeader(`Bearer ${user?.access_token}`);
};

const logout = dispatch => () => {
  dispatch(setIsImpersonationSession(false));
  userManager.signoutRedirect();
};

const App = () => {
  const dispatch = useDispatch();
  const isLoggedIn = useIsLoggedIn();
  const error = useAppError();

  useEffect(() => {
    userManager.getUser()
      .then(user => {
        const signoutRoutes = [PATH_LOGGED_OUT, PATH_LOGOUT_CALLBACK];
        const onSignoutRoute = location?.pathname && signoutRoutes.includes(location.pathname);
        return user || onSignoutRoute
          ? user
          : userManager.signinSilent();
      })
      .then(user => {
        if (user) {
          setDefaultAuthorizationHeader(`Bearer ${user.access_token}`);
          dispatch(setIsImpersonationSession(user.profile?.act?.iss === 'https://lifeway.okta.com'));
          dispatch(isLoggedInToTrue());
        } else {
          dispatch(isLoggedInToFalse());
        }
      })
      .catch(() => {
        userManager.signinRedirect();
      });
  }, [isLoggedIn]);

  useEffect(() => {
    userManager.events.addUserLoaded(userLoaded);
    userManager.events.addAccessTokenExpiring(accessTokenExpiring);
    userManager.events.addAccessTokenExpired(accessTokenExpired);
    userManager.events.addSilentRenewError(silentRenewError);
    userManager.events.addUserSignedOut(userSignedOut);

    return () => {
      userManager.events.removeUserLoaded(userLoaded);
      userManager.events.removeAccessTokenExpiring(accessTokenExpiring);
      userManager.events.removeAccessTokenExpired(accessTokenExpired);
      userManager.events.removeSilentRenewError(silentRenewError);
      userManager.events.removeUserSignedOut(userSignedOut);
    };
  }, []);

  return error
    ? <ErrorPage error={error} />
    :
    <Switch>
      <Route path={PATH_LOGIN_REDIRECT} exact component={SigninRedirectPage} />
      <Route path={AUTH_ROUTE} exact component={SigninRedirectCallbackPage} />
      <Route path={PATH_LOGOUT_CALLBACK} exact component={SignoutRedirectCallbackPage} />
      <Route path="/invoices/:invoiceId" component={InvoicePdfPage} />
      <Route path="/billing-accounts/:accountNumber/statement-dates/:date/statement" component={StatementPdfPage} />
      <Route path={PATH_INVITE_CANCELLED} exact component={InviteCancelled} />
      <Route path={PATH_REDIRECT} exact component={ExternalResourceRedirectPage} />
      <Route render={props => <Wrapper {...props} onLogOut={logout(dispatch)} />} />
    </Switch>;
};

export default App;
